import { NavigateFunction } from 'react-router-dom';
import { FilterOptions } from '../hooks/useFilters';
import { convertToQueryString } from './transformUrl';

export interface ContractProfileLocationState {
    returnTo?: string;
    fromContract?: boolean;
    returnToContractPath?: string;
    fromCommunications?: boolean;
    returnToCommunicationsPath?: string;
    fromTutorIncidences?: boolean;
    returnToTutorIncidencesPath?: string;
}

// ------------------------------------------------------------------------------------------------
/**
 * @EN Build the return to URL for the contract list
 * @ES Construye la URL de retorno para la lista de contratos
 */
// ------------------------------------------------------------------------------------------------
export const buildContractListReturnTo = (pathname: string, search: string): string =>
    `${pathname}${search}`;
// ------------------------------------------------------------------------------------------------


// ------------------------------------------------------------------------------------------------
/**
 * @EN Build the return to URL for the contract list from filters
 * @ES Construye la URL de retorno para la lista de contratos desde filtros
 */
// ------------------------------------------------------------------------------------------------
export const buildContractListReturnToFromFilters = (
    pathname: string,
    filters: FilterOptions
): string => {
    const queryString = convertToQueryString(filters);
    return queryString ? `${pathname}?${queryString}` : pathname;
};
// ------------------------------------------------------------------------------------------------


// ------------------------------------------------------------------------------------------------
/**
 * @EN Get the contract profile navigation state
 * @ES Obtiene el estado de navegación del perfil de contrato
 */
// ------------------------------------------------------------------------------------------------
export const getContractProfileNavigationState = (
    pathname: string,
    filters: FilterOptions
): ContractProfileLocationState => ({
    returnTo: buildContractListReturnToFromFilters(pathname, filters),
});
// ------------------------------------------------------------------------------------------------


// ------------------------------------------------------------------------------------------------
/**
 * @EN Resolve the contract list return to
 * @ES Resuelve la URL de retorno para la lista de contratos
 */
// ------------------------------------------------------------------------------------------------
export const resolveContractListReturnTo = (
    locationState: ContractProfileLocationState | null | undefined,
    fallbackPath: string
): string => locationState?.returnTo || fallbackPath;
// ------------------------------------------------------------------------------------------------


// ------------------------------------------------------------------------------------------------
/**
 * @EN Navigate to the contract list return
 * @ES Navega a la lista de contratos de retorno
 */
// ------------------------------------------------------------------------------------------------
export const navigateToContractListReturn = (
    navigate: NavigateFunction,
    locationState: ContractProfileLocationState | null | undefined,
    fallbackPath: string
) => {
    const returnTo = resolveContractListReturnTo(locationState, fallbackPath);

    try {
        const url = new URL(returnTo, window.location.origin);
        navigate(
            { pathname: url.pathname, search: url.search },
            { state: null }
        );
    } catch {
        navigate(returnTo, { state: null });
    }
};
// ------------------------------------------------------------------------------------------------


// ------------------------------------------------------------------------------------------------
/**
 * @EN Get the contract navigation position
 * @ES Obtiene la posición de navegación del contrato
 */
// ------------------------------------------------------------------------------------------------
export interface ContractNavigationPosition {
    index: number;
    total: number;
    hasPrevious: boolean;
    hasNext: boolean;
}
// ------------------------------------------------------------------------------------------------


// ------------------------------------------------------------------------------------------------
/**
 * @EN Get the contract navigation position
 * @ES Obtiene la posición de navegación del contrato
 */
// ------------------------------------------------------------------------------------------------
export const getContractNavigationPosition = (
    contractIds: string[],
    currentContractId: string
): ContractNavigationPosition | null => {
    const index = contractIds.indexOf(currentContractId);
    if (index === -1) {
        return null;
    }

    return {
        index,
        total: contractIds.length,
        hasPrevious: index > 0,
        hasNext: index < contractIds.length - 1,
    };
};
// ------------------------------------------------------------------------------------------------


// ------------------------------------------------------------------------------------------------
/**
 * @EN Get the adjacent contract ID
 * @ES Obtiene el ID del contrato adyacente
 */
// ------------------------------------------------------------------------------------------------
export const getAdjacentContractId = (
    contractIds: string[],
    currentContractId: string,
    direction: 'prev' | 'next'
): string | null => {
    const position = getContractNavigationPosition(contractIds, currentContractId);
    if (!position) {
        return null;
    }

    const adjacentIndex = direction === 'prev' ? position.index - 1 : position.index + 1;
    return contractIds[adjacentIndex] ?? null;
};
// ------------------------------------------------------------------------------------------------
